/**
 * Clínica universitaria: tablero del estudiante, del tutor y del director; pestaña del paciente con el caso y las
 * aprobaciones paso a paso; y la configuración (periodos, clínicas con requisitos, rúbricas y matrículas).
 */
import { useEffect, useState } from "react";
import { api, guardarSesion, leerSesion, puede, type Paciente } from "../api.ts";
import {
  notaRubrica, puedeSolicitar, NOMBRE_SOLICITUD, CODIGO_ALTA,
  type Avance, type Criterio, type Requisito, type ResumenCaso, type TipoSolicitud,
} from "../../../dominio/src/index.ts";
import { AvisoError, Seccion, useAccion } from "./comun.tsx";
import { FechaInput } from "../componentes/CampoFecha.tsx";

/* ---------------- tipos de la API ---------------- */
interface AvanceTotal { requisitos: Avance[]; porcentaje: number; cumplidos: number }
interface Matricula {
  id: string; periodo_id: string; periodo: string; acad_clinica_id: string; clinica: string; nivel: number; requisitos: Requisito[];
  estudiante_id: string; estudiante: string; tutor_id: string; tutor: string; grupo: string; activa: boolean; avance?: AvanceTotal;
}
interface Rubrica { id: string; nombre: string; criterios: Criterio[]; notaMinima: number; codigos: string[]; autor_id: string; autor?: string; activa?: boolean }
interface Solicitud {
  id: string; caso_id: string; tipo: TipoSolicitud; plan_item_id: string | null; codigo: string; descripcion: string; cantidad: number;
  estado: "pendiente" | "aprobado" | "devuelto" | "anulado"; mensaje: string; solicitante: string; solicitado_en: string;
  observacion: string; resolutor: string | null; resuelto_en: string | null; rubrica: { nombre: string; criterios: Criterio[]; notaMinima: number } | null;
  puntajes: number[] | null; nota: number | null; nombreTipo: string; con_codigo?: boolean;
  paciente_id?: string; paciente?: string; clinica?: string; estudiante?: string;
}
interface Caso {
  id: string; paciente_id: string; paciente: string; estudiante_id: string; estudiante: string; tutor_id: string; tutor: string;
  clinica: string; nivel: number; periodo: string; estado: "abierto" | "alta" | "cerrado"; creado: string; solicitudes: Solicitud[]; resumen: ResumenCaso;
}
interface ItemPlan { id: string; codigo: string; descripcion: string; diente: number | null; superficies: string; cantidad: number; estado: string; realizado_en: string | null }
interface Periodo { id: string; nombre: string; inicio: string; fin: string; activo?: boolean }
interface Config {
  activa: boolean; periodos: Required<Periodo>[]; clinicas: { id: string; nombre: string; nivel: number; descripcion: string; requisitos: Requisito[]; activa: boolean }[];
  rubricas: Rubrica[]; matriculas: Matricula[]; tutores: { id: string; nombre: string }[]; estudiantes: { id: string; nombre: string; email: string; codigo: string }[];
  aranceles: { codigo: string; nombre: string }[];
}

const fechaCorta = (iso?: string | null) => (iso ? new Date(iso.length === 10 ? iso + "T12:00:00" : iso).toLocaleDateString("es-EC", { day: "numeric", month: "short" }) : "");
const ESTADO_SOL: Record<Solicitud["estado"], [string, string]> = {
  pendiente: ["En revisión", "rojo"], aprobado: ["Aprobado", "azul"], devuelto: ["Devuelto", ""], anulado: ["Retirado", ""],
};
const yo = () => leerSesion()?.usuario;
const esTutor = () => puede("academico:tutor") || puede("academico:admin");

/* ---------------- piezas comunes ---------------- */

export function BarrasAvance({ avance }: { avance: AvanceTotal }) {
  return (
    <div className="avance">
      <div className="avance-total"><b className="mono">{avance.porcentaje} %</b><span className="muted">{avance.cumplidos} de {avance.requisitos.length} requisitos cumplidos</span></div>
      <ul>
        {avance.requisitos.map((r) => (
          <li key={r.nombre} className={r.cumplido ? "cumplido" : ""}>
            <span>{r.nombre}</span>
            <span className="barra-avance" role="progressbar" aria-valuemin={0} aria-valuemax={r.minimo} aria-valuenow={Math.min(r.hechos, r.minimo)} aria-label={r.nombre}>
              <i style={{ width: `${Math.min(100, (r.hechos / r.minimo) * 100)}%` }} />
            </span>
            <b className="mono">{r.hechos}/{r.minimo}</b>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ChipSolicitud({ s }: { s: Solicitud }) {
  const [t, c] = ESTADO_SOL[s.estado];
  return <span className={`chip ${c}`} title={s.con_codigo ? "Aprobado con el código personal del tutor, en el equipo del estudiante" : undefined}>{t}{s.nota !== null && s.tipo === "validacion" ? ` · ${s.nota.toFixed(2)}` : ""}{s.con_codigo ? " · con código" : ""}</span>;
}

/** El tutor aprueba o devuelve; la validación se califica con una rúbrica. */
function Resolver({ s, rubricas, alResolver, conCodigo = false }: { s: Solicitud; rubricas: Rubrica[]; alResolver: (c: Caso) => void; conCodigo?: boolean }) {
  const [codigo, setCodigo] = useState("");
  const sugeridas = rubricas.filter((r) => !r.codigos.length || r.codigos.includes(s.codigo));
  const [rid, setRid] = useState(s.tipo === "validacion" ? (sugeridas.find((r) => r.codigos.includes(s.codigo)) ?? sugeridas[0])?.id ?? "" : "");
  const rub = rubricas.find((r) => r.id === rid) ?? null;
  const [pts, setPts] = useState<string[]>([]);
  const [obs, setObs] = useState("");
  const a = useAccion();
  const puntajes = (rub?.criterios ?? []).map((_, k) => (pts[k] ?? "").trim() === "" ? NaN : Number(String(pts[k]).replace(",", ".")));
  const calif = rub ? notaRubrica(rub, puntajes) : null;
  const completa = !!rub && puntajes.every((x) => Number.isFinite(x));
  const enviar = async (decision: "aprobar" | "devolver") => {
    const r = await a.correr(() => api<Caso>("POST", `/api/academico/solicitudes/${s.id}/${conCodigo ? "resolver-con-codigo" : "resolver"}`, {
      ...(conCodigo ? { codigo } : {}), decision, observacion: obs, ...(s.tipo === "validacion" && rub && (decision === "aprobar" || completa) ? { rubrica_id: rub.id, puntajes } : {}),
    }));
    if (r) alResolver(r);
  };
  return (
    <div className="resolver">
      {conCodigo && (
        <label className="campo">Código personal del tutor/a (lo escribe el tutor/a)
          <input className="entrada mono" type="password" inputMode="numeric" autoComplete="off" maxLength={6} placeholder="6 dígitos" value={codigo} onChange={(e) => setCodigo(e.target.value.replace(/\D/g, ""))} style={{ maxWidth: 160 }} />
        </label>
      )}
      {s.tipo === "validacion" && (
        <>
          <label className="campo">Rúbrica
            <select value={rid} onChange={(e) => { setRid(e.target.value); setPts([]); }}>
              {!rubricas.length && <option value="">No hay rúbricas: crea una</option>}
              {rubricas.map((r) => <option key={r.id} value={r.id}>{r.nombre}{r.codigos.includes(s.codigo) ? " · sugerida" : ""}</option>)}
            </select>
          </label>
          {rub && (
            <table className="tabla-rubrica">
              <thead><tr><th>Criterio</th><th>Máx.</th><th>Puntaje</th></tr></thead>
              <tbody>{rub.criterios.map((c, k) => (
                <tr key={k}><td>{c.nombre}</td><td className="mono">{c.maximo}</td>
                  <td><input className="entrada mono" inputMode="decimal" aria-label={`Puntaje de ${c.nombre}`} value={pts[k] ?? ""}
                    onChange={(e) => { const x = [...pts]; x[k] = e.target.value; setPts(x); }} /></td></tr>
              ))}</tbody>
              <tfoot><tr><td colSpan={2}>Nota (sobre 10) · mínima {rub.notaMinima}</td>
                <td><b className={`mono ${completa ? (calif!.aprueba ? "ok-texto" : "mal-texto") : ""}`}>{completa ? calif!.nota.toFixed(2) : "—"}</b></td></tr></tfoot>
            </table>
          )}
        </>
      )}
      <label className="campo">Observación para el estudiante{s.tipo === "validacion" ? "" : " (opcional al aprobar)"}
        <textarea rows={2} maxLength={1000} value={obs} onChange={(e) => setObs(e.target.value)} placeholder="Qué está bien y qué debe corregir" /></label>
      <AvisoError error={a.error} />
      <div className="fila">
        <button className="btn pri" disabled={a.ocupado || (conCodigo && codigo.length !== 6) || (s.tipo === "validacion" && (!completa || !calif?.aprueba))} onClick={() => enviar("aprobar")}>
          {s.tipo === "validacion" ? "Validar" : s.tipo === "autorizacion" ? "Autorizar" : s.tipo === "alta" ? "Dar de alta" : "Aprobar"}</button>
        <button className="btn" disabled={a.ocupado || (conCodigo && codigo.length !== 6) || !obs.trim()} onClick={() => enviar("devolver")}>Devolver para corregir</button>
      </div>
    </div>
  );
}

/** El código de hoy del tutor: cambia solo cada día a las 00:00. Con él aprueba un procedimiento en el equipo del estudiante, sin cerrar la sesión de este. */
function CodigoTutor() {
  const [e, setE] = useState<{ codigo: string; bloqueado: boolean } | null>(null);
  const a = useAccion();
  const cargar = () => api<{ codigo: string; bloqueado: boolean }>("GET", "/api/academico/mi-codigo").then(setE).catch(() => undefined);
  useEffect(() => { cargar(); }, []);
  async function cambiar() {
    if (!confirm("¿Cambiar el código de hoy? El actual deja de servir de inmediato.")) return;
    const r = await a.correr(() => api<{ codigo: string; bloqueado: boolean }>("POST", "/api/academico/mi-codigo"));
    if (r) setE(r);
  }
  if (!e) return null;
  return (
    <Seccion titulo="Mi código de hoy" acciones={<span className="chip azul">Cambia cada día a las 00:00</span>}>
      <p className="muted" style={{ margin: 0, fontSize: 14 }}>Con este código de 6 dígitos apruebas un procedimiento directamente en el equipo de tu estudiante («Aprobar con código del tutor»), sin entrar a tu sesión. Es personal: no lo compartas. Mañana será otro. Cinco errores seguidos lo bloquean 15 minutos.</p>
      <div className="aviso ok" role="status">Código de hoy: <b className="mono" style={{ fontSize: 24, letterSpacing: 4 }}>{e.codigo}</b></div>
      {e.bloqueado && <div className="aviso error">Tu código está bloqueado unos minutos por intentos fallidos.</div>}
      <AvisoError error={a.error} />
      <div><button className="btn chico" disabled={a.ocupado} onClick={cambiar}>Cambiar el código de hoy</button></div>
    </Seccion>
  );
}

/* ================= TABLERO ================= */

interface Tablero {
  activa: boolean; periodo: Periodo | null; rol: string;
  matriculas?: Matricula[]; casos?: Caso[]; pacientes?: { paciente_id: string; paciente: string }[]; solicitudes?: Solicitud[];
  pendientes?: Solicitud[]; clinicas?: { clinica: string; nivel: number; estudiantes: number; porcentaje: number; cumplen: number }[];
}

export function TableroUniversidad({ ir }: { ir: (r: string) => void }) {
  const [t, setT] = useState<Tablero | null>(null);
  const [cfg, setCfg] = useState<Config | null>(null);
  const [abierta, setAbierta] = useState<string | null>(null);
  const a = useAccion();
  const cargar = () => {
    api<Tablero>("GET", "/api/academico/tablero").then(setT).catch(a.setError);
    api<Config>("GET", "/api/academico/config").then(setCfg).catch(() => undefined);
  };
  useEffect(cargar, []);
  if (!t) return <Seccion titulo="Clínica universitaria"><AvisoError error={a.error} />{!a.error && <p className="muted">Cargando…</p>}</Seccion>;
  if (!t.activa) return <Seccion titulo="Clínica universitaria"><p className="muted">La clínica universitaria no está activada.</p></Seccion>;
  const periodo = t.periodo ? <span className="muted">{t.periodo.nombre} · {fechaCorta(t.periodo.inicio)} al {fechaCorta(t.periodo.fin)}</span> : <span className="aviso error" style={{ display: "inline-block" }}>No hay un periodo activo.</span>;
  const soyEstudiante = t.rol === "estudiante";
  const mias = (cfg?.rubricas ?? []).filter((r) => r.autor_id === yo()?.id);

  if (soyEstudiante) return (
    <div className="grid-universidad">
      <Seccion titulo="Mi avance" acciones={periodo}>
        {!t.matriculas?.length && <p className="muted">No estás matriculado en ninguna clínica este periodo. Pídeselo al director de clínicas.</p>}
        {t.matriculas?.map((m) => (
          <div key={m.id} className="tarjeta-matricula">
            <h3>{m.clinica} <span className="muted">· nivel {m.nivel}{m.grupo ? ` · grupo ${m.grupo}` : ""} · tutor/a {m.tutor}</span></h3>
            {m.avance && <BarrasAvance avance={m.avance} />}
          </div>
        ))}
      </Seccion>
      <Seccion titulo="Mis casos abiertos">
        {!t.casos?.length && <p className="muted">Sin casos abiertos. Abre uno desde la pestaña «Clínica universitaria» de tu paciente.</p>}
        <ul className="lista">{t.casos?.map((k) => (
          <li key={k.id}><button onClick={() => ir(`paciente/${k.paciente_id}/universidad`)}><b>{k.paciente}</b><span>{k.clinica}</span></button></li>
        ))}</ul>
        {!!t.pacientes?.length && <p className="muted" style={{ margin: 0, fontSize: 13 }}>Tus pacientes: {t.pacientes.map((x) => x.paciente).join(", ")}.</p>}
      </Seccion>
      <Seccion titulo="Mis solicitudes">
        {!t.solicitudes?.length && <p className="muted">Aún no has pedido aprobaciones.</p>}
        <ul className="lista-solicitudes-acad">{t.solicitudes?.map((s) => (
          <li key={s.id}>
            <button className="enlace" onClick={() => ir(`paciente/${s.paciente_id}/universidad`)}><b>{s.paciente}</b></button> · {s.nombreTipo}{s.descripcion ? ` · ${s.descripcion}` : ""} <ChipSolicitud s={s} />
            {s.observacion && <div className="muted obs">«{s.observacion}» — {s.resolutor}</div>}
          </li>
        ))}</ul>
      </Seccion>
    </div>
  );

  // tutor o director
  const pendientes = t.pendientes ?? [];
  return (
    <div className="grid-universidad">
      <Seccion titulo={`Por revisar (${pendientes.length})`} acciones={periodo}>
        {!pendientes.length && <p className="muted">No hay solicitudes pendientes.</p>}
        <ul className="lista-solicitudes-acad">{pendientes.map((s) => (
          <li key={s.id}>
            <div className="fila" style={{ justifyContent: "space-between", gap: 8 }}>
              <span><b>{s.estudiante}</b> · {s.nombreTipo}{s.descripcion ? ` · ${s.descripcion}` : ""}<br />
                <span className="muted">{s.paciente} · {s.clinica} · {fechaCorta(s.solicitado_en)}{s.mensaje ? ` · «${s.mensaje}»` : ""}</span></span>
              <span className="fila" style={{ gap: 6 }}>
                <button className="btn chico" onClick={() => ir(`paciente/${s.paciente_id}/universidad`)}>Ver paciente</button>
                <button className="btn chico pri" aria-expanded={abierta === s.id} onClick={() => setAbierta(abierta === s.id ? null : s.id)}>Revisar</button>
              </span>
            </div>
            {abierta === s.id && <Resolver s={s} rubricas={cfg?.rubricas.filter((r) => r.activa !== false) ?? []} alResolver={() => { setAbierta(null); cargar(); }} />}
          </li>
        ))}</ul>
      </Seccion>
      {puede("academico:admin") && !!t.clinicas?.length && (
        <Seccion titulo="Resumen por clínica" acciones={<button className="btn chico" onClick={() => descargarCsv(a.setError)}>Descargar avance (CSV)</button>}>
          <div className="tiles">{t.clinicas.map((x) => (
            <div key={x.clinica} className="tile"><span>{x.clinica} · nivel {x.nivel}</span><b>{x.porcentaje} %</b><small>{x.estudiantes} estudiante{x.estudiantes === 1 ? "" : "s"} · {x.cumplen} con todo cumplido</small></div>
          ))}</div>
          <AvisoError error={a.error} />
        </Seccion>
      )}
      <Seccion titulo={puede("academico:admin") ? "Avance de los estudiantes" : "Mis estudiantes"}>
        {!t.matriculas?.length && <p className="muted">Sin estudiantes matriculados en el periodo activo.</p>}
        <div className="tabla-scroll"><table className="tabla-resp tabla-avance">
          <thead><tr><th>Estudiante</th><th>Clínica</th>{puede("academico:admin") && <th>Tutor/a</th>}<th>Avance</th><th>Pendiente</th></tr></thead>
          <tbody>{t.matriculas?.map((m) => (
            <tr key={m.id}>
              <td><b>{m.estudiante}</b>{m.grupo && <span className="muted"> · {m.grupo}</span>}</td>
              <td>{m.clinica}</td>
              {puede("academico:admin") && <td>{m.tutor}</td>}
              <td><span className="barra-avance ancha"><i style={{ width: `${m.avance?.porcentaje ?? 0}%` }} /></span> <b className="mono">{m.avance?.porcentaje ?? 0} %</b></td>
              <td className="muted" style={{ fontSize: 13 }}>{m.avance?.requisitos.filter((r) => !r.cumplido).map((r) => `${r.nombre} ${r.hechos}/${r.minimo}`).join(" · ") || "Todo cumplido"}</td>
            </tr>
          ))}</tbody>
        </table></div>
      </Seccion>
      {puede("academico:tutor") && <CodigoTutor />}
      {esTutor() && cfg && <Rubricas lista={puede("academico:admin") ? cfg.rubricas : mias} aranceles={cfg.aranceles} titulo={puede("academico:admin") ? "Rúbricas" : "Mis rúbricas"} alCambiar={cargar} />}
    </div>
  );
}

async function descargarCsv(alError: (e: unknown) => void) {
  try {
    const token = leerSesion()?.token;
    const r = await fetch("/api/academico/avance.csv", { headers: { authorization: `Bearer ${token}` } });
    if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error ?? "No se pudo descargar.");
    const url = URL.createObjectURL(await r.blob());
    const x = document.createElement("a"); x.href = url; x.download = `avance-clinica-universitaria.csv`; x.click();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  } catch (e) { alError(e); }
}

/* ---------------- rúbricas ---------------- */

function Rubricas({ lista, aranceles, titulo, alCambiar }: { lista: Rubrica[]; aranceles: Config["aranceles"]; titulo: string; alCambiar: () => void }) {
  const [editando, setEditando] = useState<Rubrica | null>(null);
  return (
    <Seccion titulo={titulo} acciones={!editando && <button className="btn chico pri" onClick={() => setEditando({ id: "", nombre: "", criterios: [{ nombre: "", maximo: 2 }], notaMinima: 7, codigos: [], autor_id: "" })}>Nueva rúbrica</button>}>
      {editando ? <EditorRubrica inicial={editando} aranceles={aranceles} alTerminar={() => { setEditando(null); alCambiar(); }} /> : (
        <>
          {!lista.length && <p className="muted">Aún no hay rúbricas. Crea la tuya: cada criterio con su puntaje máximo; la nota va sobre 10.</p>}
          <ul className="lista">{lista.map((r) => (
            <li key={r.id}><button onClick={() => setEditando(r)}>
              <b>{r.nombre}</b><span>{r.criterios.length} criterios · mínima {r.notaMinima}{r.codigos.length ? ` · ${r.codigos.join(", ")}` : ""}{r.autor ? ` · ${r.autor}` : ""}</span>
              {r.activa === false && <span className="chip" style={{ marginLeft: "auto" }}>Inactiva</span>}
            </button></li>
          ))}</ul>
        </>
      )}
    </Seccion>
  );
}

function EditorRubrica({ inicial, aranceles, alTerminar }: { inicial: Rubrica; aranceles: Config["aranceles"]; alTerminar: () => void }) {
  const [r, setR] = useState({ ...inicial, criterios: inicial.criterios.map((c) => ({ nombre: c.nombre, maximo: String(c.maximo) })), notaMinima: String(inicial.notaMinima), activa: inicial.activa !== false });
  const a = useAccion();
  const total = r.criterios.reduce((s, c) => s + (Number(c.maximo.replace(",", ".")) || 0), 0);
  const guardar = async () => {
    const cuerpo = { nombre: r.nombre, notaMinima: Number(r.notaMinima.replace(",", ".")), codigos: r.codigos, activa: r.activa,
      criterios: r.criterios.map((c) => ({ nombre: c.nombre, maximo: Number(c.maximo.replace(",", ".")) })) };
    const ok = await a.correr(() => api(inicial.id ? "PUT" : "POST", inicial.id ? `/api/academico/rubricas/${inicial.id}` : "/api/academico/rubricas", cuerpo));
    if (ok) alTerminar();
  };
  return (
    <div className="form-factura">
      <div className="fila">
        <label className="campo" style={{ flex: "2 1 240px" }}>Nombre<input value={r.nombre} maxLength={80} onChange={(e) => setR({ ...r, nombre: e.target.value })} placeholder="Resinas clase II" /></label>
        <label className="campo" style={{ flex: "0 1 140px" }}>Nota mínima (/10)<input className="mono" inputMode="decimal" value={r.notaMinima} onChange={(e) => setR({ ...r, notaMinima: e.target.value })} /></label>
      </div>
      <fieldset><legend>Criterios · total {total} puntos (la nota se lleva a 10)</legend>
        {r.criterios.map((c, k) => (
          <div key={k} className="fila">
            <label className="campo" style={{ flex: "3 1 240px" }}>Criterio {k + 1}<input value={c.nombre} maxLength={120} onChange={(e) => setR({ ...r, criterios: r.criterios.map((x, j) => (j === k ? { ...x, nombre: e.target.value } : x)) })} /></label>
            <label className="campo" style={{ flex: "0 1 110px" }}>Puntaje máx.<input className="mono" inputMode="decimal" value={c.maximo} onChange={(e) => setR({ ...r, criterios: r.criterios.map((x, j) => (j === k ? { ...x, maximo: e.target.value } : x)) })} /></label>
            <button type="button" className="btn chico" aria-label={`Quitar el criterio ${k + 1}`} onClick={() => setR({ ...r, criterios: r.criterios.filter((_, j) => j !== k) })}>✕</button>
          </div>
        ))}
        <button type="button" className="btn chico" onClick={() => setR({ ...r, criterios: [...r.criterios, { nombre: "", maximo: "2" }] })}>+ Criterio</button>
      </fieldset>
      <SelectorCodigos titulo="Se sugiere para estos procedimientos (opcional)" valor={r.codigos} aranceles={aranceles} alCambiar={(codigos) => setR({ ...r, codigos })} />
      {inicial.id && <label className="firmar"><input type="checkbox" checked={r.activa} onChange={(e) => setR({ ...r, activa: e.target.checked })} /> Rúbrica activa</label>}
      <AvisoError error={a.error} />
      <div className="fila"><button className="btn pri" disabled={a.ocupado} onClick={guardar}>Guardar rúbrica</button><button className="btn" onClick={alTerminar}>Cancelar</button></div>
    </div>
  );
}

function SelectorCodigos({ titulo, valor, aranceles, alCambiar, conAlta }: { titulo: string; valor: string[]; aranceles: Config["aranceles"]; alCambiar: (v: string[]) => void; conAlta?: boolean }) {
  const opciones = [...(conAlta ? [{ codigo: CODIGO_ALTA, nombre: "Paciente dado de alta" }] : []), ...aranceles];
  return (
    <details className="selector-codigos">
      <summary>{titulo}: {valor.length ? valor.join(", ") : "ninguno"}</summary>
      <div className="codigos">{opciones.map((x) => (
        <label key={x.codigo} className="chip-codigo"><input type="checkbox" checked={valor.includes(x.codigo)} onChange={(e) => alCambiar(e.target.checked ? [...valor, x.codigo] : valor.filter((k) => k !== x.codigo))} />
          <b className="mono">{x.codigo}</b> {x.nombre}</label>
      ))}</div>
    </details>
  );
}

/* ================= PESTAÑA DEL PACIENTE ================= */

interface DatosPaciente {
  activa: boolean; periodo?: Periodo | null; dueno?: { estudiante_id: string; estudiante: string; activo: boolean; asignado_en: string } | null;
  casos?: Caso[]; matriculas?: Matricula[]; rubricas?: Rubrica[]; items?: ItemPlan[]; motivoNoCeder?: string | null;
  estudiantes?: { id: string; nombre: string }[]; cesiones?: { creado: string; de: string | null; a: string; motivo: string }[];
}

export function PestanaUniversidad({ paciente, irA }: { paciente: Paciente; irA: (t: string) => void }) {
  const [d, setD] = useState<DatosPaciente | null>(null);
  const [mat, setMat] = useState("");
  const a = useAccion();
  const url = `/api/pacientes/${paciente.id}/academico`;
  const cargar = () => api<DatosPaciente>("GET", url).then((x) => { setD(x); setMat((m) => m || x.matriculas?.[0]?.id || ""); }).catch(a.setError);
  useEffect(() => { cargar(); }, [paciente.id]);
  if (!d) return <AvisoError error={a.error} />;
  if (!d.activa) return <Seccion titulo="Clínica universitaria"><p className="muted">La clínica universitaria no está activada.</p></Seccion>;
  const abiertos = (d.casos ?? []).filter((k) => k.estado === "abierto");
  const libres = (d.matriculas ?? []).filter((m) => !abiertos.some((k) => k.clinica === m.clinica));
  const u = yo();
  const puedeAbrir = u?.rol === "estudiante" ? !d.dueno || d.dueno.estudiante_id === u.id : esTutor();
  return (
    <div style={{ display: "grid", gap: 16 }}>
      <Seccion titulo="Clínica universitaria" acciones={d.periodo ? <span className="muted">{d.periodo.nombre}</span> : <span className="chip rojo">Sin periodo activo</span>}>
        <p style={{ margin: 0 }}>{d.dueno ? <>Paciente de <b>{d.dueno.estudiante}</b> desde el {fechaCorta(d.dueno.asignado_en)}{!d.dueno.activo && <span className="chip" style={{ marginLeft: 6 }}>estudiante inactivo</span>}.</> : <span className="muted">Todavía no está asignado a un estudiante: queda con quien abra el primer caso.</span>}</p>
        {puedeAbrir && libres.length > 0 && (
          <div className="fila" style={{ alignItems: "flex-end" }}>
            <label className="campo" style={{ flex: "0 1 320px" }}>Abrir caso en
              <select value={libres.some((m) => m.id === mat) ? mat : libres[0].id} onChange={(e) => setMat(e.target.value)}>
                {libres.map((m) => <option key={m.id} value={m.id}>{m.clinica} · {m.estudiante} (tutor/a {m.tutor})</option>)}
              </select></label>
            <button className="btn pri" disabled={a.ocupado} onClick={async () => {
              const id = libres.some((m) => m.id === mat) ? mat : libres[0].id;
              if (await a.correr(() => api("POST", `${url}/casos`, { matricula_id: id }))) cargar();
            }}>Abrir caso</button>
          </div>
        )}
        {puede("academico:admin") && <Ceder d={d} url={url} alCambiar={cargar} />}
        <AvisoError error={a.error} />
      </Seccion>
      {(d.casos ?? []).map((k) => <CasoAcademico key={k.id} k={k} items={d.items ?? []} rubricas={d.rubricas ?? []} irA={irA} alCambiar={cargar} />)}
    </div>
  );
}

function Ceder({ d, url, alCambiar }: { d: DatosPaciente; url: string; alCambiar: () => void }) {
  const [abierto, setAbierto] = useState(false);
  const [est, setEst] = useState(""), [motivo, setMotivo] = useState("");
  const a = useAccion();
  if (!abierto) return <div><button className="btn chico" onClick={() => setAbierto(true)}>{d.dueno ? "Ceder a otro estudiante" : "Asignar a un estudiante"}</button></div>;
  return (
    <div className="card" style={{ boxShadow: "none", background: "var(--surface-2)" }}>
      {d.motivoNoCeder && <div className="aviso info" role="status">{d.motivoNoCeder}</div>}
      <div className="fila" style={{ alignItems: "flex-end" }}>
        <label className="campo" style={{ flex: "1 1 220px" }}>Estudiante<select value={est} onChange={(e) => setEst(e.target.value)}><option value="">Elige…</option>{(d.estudiantes ?? []).filter((x) => x.id !== d.dueno?.estudiante_id).map((x) => <option key={x.id} value={x.id}>{x.nombre}</option>)}</select></label>
        <label className="campo" style={{ flex: "2 1 260px" }}>Motivo<input value={motivo} maxLength={300} onChange={(e) => setMotivo(e.target.value)} placeholder="Graduación de …, cambio de semestre…" /></label>
        <button className="btn pri" disabled={a.ocupado || !est || !!d.motivoNoCeder} onClick={async () => { if (await a.correr(() => api("POST", `${url}/asignar`, { estudiante_id: est, motivo }))) { setAbierto(false); alCambiar(); } }}>{d.dueno ? "Ceder" : "Asignar"}</button>
        <button className="btn" onClick={() => setAbierto(false)}>Cancelar</button>
      </div>
      {!!d.cesiones?.length && <p className="muted" style={{ margin: 0, fontSize: 13 }}>Historial: {d.cesiones.map((c) => `${fechaCorta(c.creado)} ${c.de ? `${c.de} → ` : ""}${c.a}${c.motivo ? ` (${c.motivo})` : ""}`).join(" · ")}</p>}
      <AvisoError error={a.error} />
    </div>
  );
}

function CasoAcademico({ k, items, rubricas, irA, alCambiar }: { k: Caso; items: ItemPlan[]; rubricas: Rubrica[]; irA: (t: string) => void; alCambiar: () => void }) {
  const [msg, setMsg] = useState("");
  const [revisar, setRevisar] = useState<string | null>(null);
  const [conCodigoDe, setConCodigoDe] = useState<string | null>(null);
  const a = useAccion();
  const u = yo();
  const soyEstudiante = u?.id === k.estudiante_id;
  const soyTutor = u?.id === k.tutor_id || puede("academico:admin");
  const abierto = k.estado === "abierto";
  const r = k.resumen;
  const idsCaso = new Set(k.solicitudes.filter((s) => s.plan_item_id && s.estado !== "anulado").map((s) => s.plan_item_id));
  const pendientesPlan = items.filter((x) => idsCaso.has(x.id) && !r.items[x.id]?.validado).length;
  const pedir = async (tipo: TipoSolicitud, itemId?: string) => {
    const x = await a.correr(() => api("POST", `/api/academico/casos/${k.id}/solicitudes`, { tipo, plan_item_id: itemId ?? null, mensaje: msg }));
    if (x) { setMsg(""); alCambiar(); }
  };
  const retirar = async (s: Solicitud) => { if (await a.correr(() => api("POST", `/api/academico/solicitudes/${s.id}/anular`))) alCambiar(); };
  const pendienteDe = (tipo: TipoSolicitud, itemId?: string) => k.solicitudes.find((s) => s.estado === "pendiente" && s.tipo === tipo && (itemId ? s.plan_item_id === itemId : true));
  const ultimaDe = (tipo: TipoSolicitud) => [...k.solicitudes].reverse().find((s) => s.tipo === tipo && s.estado !== "anulado");

  const accionesPaso = (tipo: "historia" | "plan" | "alta") => {
    const pend = pendienteDe(tipo);
    const motivo = puedeSolicitar(r, tipo, null, pendientesPlan);
    return (
      <>
        {pend && soyTutor && <button className="btn chico pri" onClick={() => setRevisar(revisar === pend.id ? null : pend.id)}>Revisar</button>}
        {pend && soyEstudiante && <button className="btn chico" onClick={() => setConCodigoDe(conCodigoDe === pend.id ? null : pend.id)} title="Tu tutor/a escribe su código personal en este equipo">Aprobar con código del tutor</button>}
        {pend && soyEstudiante && <button className="btn chico" onClick={() => retirar(pend)}>Retirar</button>}
        {!pend && soyEstudiante && abierto && !r[tipo].aprobado && (
          <button className="btn chico pri" disabled={!!motivo || a.ocupado} title={motivo ?? ""} onClick={() => pedir(tipo)}>
            {tipo === "historia" ? "Enviar historia a revisión" : tipo === "plan" ? "Enviar plan a revisión" : "Pedir el alta"}</button>
        )}
      </>
    );
  };
  const paso = (n: number, tipo: "historia" | "plan" | "alta", ir?: [string, string]) => {
    const ult = ultimaDe(tipo);
    const pend = pendienteDe(tipo);
    return (
      <li className={`paso-acad ${r[tipo].aprobado ? "hecho" : pend ? "espera" : ""}`}>
        <span className="num">{r[tipo].aprobado ? "✓" : n}</span>
        <div>
          <b>{NOMBRE_SOLICITUD[tipo]}</b> {ult && <ChipSolicitud s={ult} />}
          {ult?.observacion && ult.estado !== "pendiente" && <div className="muted obs">«{ult.observacion}» — {ult.resolutor}</div>}
          {ir && <button className="enlace" onClick={() => irA(ir[0])}>{ir[1]}</button>}
          {revisar && pend?.id === revisar && <Resolver s={pend} rubricas={rubricas} alResolver={() => { setRevisar(null); alCambiar(); }} />}
          {conCodigoDe && pend?.id === conCodigoDe && <Resolver conCodigo s={pend} rubricas={rubricas} alResolver={() => { setConCodigoDe(null); alCambiar(); }} />}
        </div>
        <span className="acciones-paso">{accionesPaso(tipo)}</span>
      </li>
    );
  };
  const itemsVisibles = r.plan.aprobado ? items : [];
  return (
    <Seccion titulo={`${k.clinica} · ${k.estudiante}`} acciones={<span className={`chip ${abierto ? "rojo" : "azul"}`}>{abierto ? "Abierto" : k.estado === "alta" ? "Alta" : "Cerrado"}</span>}>
      <p className="muted" style={{ margin: 0 }}>Tutor/a {k.tutor} · {k.periodo} · abierto el {fechaCorta(k.creado)}</p>
      {soyEstudiante && abierto && <label className="campo">Mensaje para tu tutor/a (opcional)<input value={msg} maxLength={500} onChange={(e) => setMsg(e.target.value)} placeholder="Por ejemplo: pieza 36 lista para revisar" /></label>}
      <ol className="pasos-acad">
        {paso(1, "historia", ["ficha", "Abrir la historia clínica"])}
        {paso(2, "plan", ["plan", "Abrir el plan de tratamiento"])}
        <li className={`paso-acad ${r.plan.aprobado && itemsVisibles.length && itemsVisibles.every((x) => !idsCaso.has(x.id) || r.items[x.id]?.validado) && idsCaso.size ? "hecho" : ""}`}>
          <span className="num">3</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <b>Procedimientos</b> <span className="muted">— autorización antes y validación con rúbrica al terminar</span>
            {!r.plan.aprobado && <div className="muted">Se habilitan cuando el tutor aprueba el plan.</div>}
            {r.plan.aprobado && !items.length && <div className="muted">El plan no tiene procedimientos.</div>}
            <ul className="items-acad">{itemsVisibles.map((it) => {
              const x = r.items[it.id];
              const pa = pendienteDe("autorizacion", it.id), pv = pendienteDe("validacion", it.id);
              const pend = pa ?? pv;
              const deOtro = !x && it.estado === "realizado";
              const motA = puedeSolicitar(r, "autorizacion", { id: it.id, realizado: it.estado === "realizado" });
              const motV = puedeSolicitar(r, "validacion", { id: it.id, realizado: it.estado === "realizado" });
              return (
                <li key={it.id}>
                  <div className="fila" style={{ justifyContent: "space-between", gap: 8 }}>
                    <span><b className="mono">{it.codigo}</b> {it.descripcion}{it.diente ? ` · pieza ${it.diente}` : ""}{it.cantidad > 1 ? ` × ${it.cantidad}` : ""}{" "}
                      {x?.validado ? <span className="chip azul">Validado{x.nota !== null ? ` · ${Number(x.nota).toFixed(2)}` : ""}</span>
                        : pend ? <span className="chip rojo">{pa ? "Autorización en revisión" : "Validación en revisión"}</span>
                        : x?.autorizado ? <span className="chip">Autorizado</span> : deOtro ? <span className="chip">Realizado fuera del caso</span> : null}
                    </span>
                    <span className="fila" style={{ gap: 6 }}>
                      {pend && soyTutor && <button className="btn chico pri" onClick={() => setRevisar(revisar === pend.id ? null : pend.id)}>Revisar</button>}
                      {pend && soyEstudiante && <button className="btn chico" onClick={() => setConCodigoDe(conCodigoDe === pend.id ? null : pend.id)} title="Tu tutor/a escribe su código personal en este equipo">Aprobar con código del tutor</button>}
                      {pend && soyEstudiante && <button className="btn chico" onClick={() => retirar(pend)}>Retirar</button>}
                      {!pend && soyEstudiante && abierto && !x?.validado && !deOtro && (!x?.autorizado
                        ? <button className="btn chico" disabled={!!motA || a.ocupado} title={motA ?? ""} onClick={() => pedir("autorizacion", it.id)}>Pedir autorización</button>
                        : <button className="btn chico pri" disabled={!!motV || a.ocupado} title={motV ?? ""} onClick={() => pedir("validacion", it.id)}>Pedir validación</button>)}
                    </span>
                  </div>
                  {x?.ultimaObservacion && !x.validado && !pend && <div className="muted obs">«{x.ultimaObservacion}»</div>}
                  {pend && revisar === pend.id && <Resolver s={pend} rubricas={rubricas} alResolver={() => { setRevisar(null); alCambiar(); }} />}
                  {pend && conCodigoDe === pend.id && <Resolver conCodigo s={pend} rubricas={rubricas} alResolver={() => { setConCodigoDe(null); alCambiar(); }} />}
                </li>
              );
            })}</ul>
          </div>
        </li>
        {paso(4, "alta")}
      </ol>
      <details className="historial-acad">
        <summary>Historial del caso ({k.solicitudes.length})</summary>
        <ul>{[...k.solicitudes].reverse().map((s) => (
          <li key={s.id}>
            <span className="muted mono">{fechaCorta(s.solicitado_en)}</span> {s.nombreTipo}{s.descripcion ? ` · ${s.descripcion}` : ""} <ChipSolicitud s={s} />
            {s.mensaje && <div className="muted obs">{s.solicitante}: «{s.mensaje}»</div>}
            {s.observacion && <div className="muted obs">{s.resolutor}: «{s.observacion}»</div>}
            {s.rubrica && s.puntajes && <div className="muted obs">{s.rubrica.nombre}: {s.rubrica.criterios.map((c, j) => `${c.nombre} ${s.puntajes![j]}/${c.maximo}`).join(" · ")}</div>}
          </li>
        ))}</ul>
      </details>
      {soyTutor && abierto && <div><button className="btn chico peligro" disabled={a.ocupado} onClick={async () => { if (confirm("¿Cerrar el caso sin alta? Las solicitudes pendientes se anulan.")) { if (await a.correr(() => api("POST", `/api/academico/casos/${k.id}/cerrar`))) alCambiar(); } }}>Cerrar caso sin alta</button></div>}
      <AvisoError error={a.error} />
    </Seccion>
  );
}

/* ================= CONFIGURACIÓN (director) ================= */

export function ConfigUniversidad() {
  const [c, setC] = useState<Config | null>(null);
  const [ok, setOk] = useState("");
  const a = useAccion();
  const cargar = () => api<Config>("GET", "/api/academico/config").then(setC).catch(a.setError);
  useEffect(() => { cargar(); }, []);
  if (!c) return <Seccion titulo="Clínica universitaria"><AvisoError error={a.error} />{!a.error && <p className="muted">Cargando…</p>}</Seccion>;
  const activar = async (activa: boolean) => {
    if (await a.correr(() => api("PUT", "/api/academico/activar", { activa }))) {
      const s = leerSesion(); if (s) guardarSesion({ ...s, usuario: { ...s.usuario, academico: activa } });
      location.reload();
    }
  };
  return (
    <div style={{ display: "grid", gap: 16 }}>
      <Seccion titulo="Clínica universitaria">
        <p style={{ margin: 0 }}>Para facultades de odontología: el <b>estudiante</b> atiende y el <b>tutor</b> aprueba cada paso (historia clínica, plan, autorización y validación de cada procedimiento con su rúbrica, y alta). Los procedimientos validados cuentan para los requisitos mínimos de cada clínica.</p>
        <label className="firmar"><input type="checkbox" checked={c.activa} disabled={a.ocupado} onChange={(e) => activar(e.target.checked)} /> Clínica universitaria activada</label>
        {c.activa && !c.clinicas.length && (
          <div className="aviso info">
            Empieza con la <b>plantilla de ejemplo</b> (Integral I y II, Niños y Cirugía con requisitos de muestra y una rúbrica general) y ajústala a las tablas de mínimos de tu facultad.
            <div style={{ marginTop: 8 }}><button className="btn chico pri" disabled={a.ocupado} onClick={async () => { const r = await a.correr(() => api<{ clinicas: number }>("POST", "/api/academico/plantilla")); if (r) { setOk(`Plantilla cargada: ${r.clinicas} clínicas. Revisa los requisitos.`); cargar(); } }}>Cargar plantilla de ejemplo</button></div>
          </div>
        )}
        {ok && <div className="aviso ok" role="status">{ok}</div>}
        <AvisoError error={a.error} />
      </Seccion>
      {c.activa && <>
        <Periodos c={c} alCambiar={cargar} />
        <Clinicas c={c} alCambiar={cargar} />
        <Matriculas c={c} alCambiar={cargar} />
        <Rubricas lista={c.rubricas} aranceles={c.aranceles} titulo="Rúbricas" alCambiar={cargar} />
      </>}
    </div>
  );
}

function Periodos({ c, alCambiar }: { c: Config; alCambiar: () => void }) {
  const vacio = { id: "", nombre: "", inicio: "", fin: "", activo: !c.periodos.length };
  const [f, setF] = useState<Required<Periodo> | null>(null);
  const a = useAccion();
  return (
    <Seccion titulo="Periodos académicos" acciones={!f && <button className="btn chico pri" onClick={() => setF(vacio)}>Nuevo periodo</button>}>
      {f && (
        <div className="fila" style={{ alignItems: "flex-end" }}>
          <label className="campo" style={{ flex: "2 1 200px" }}>Nombre<input value={f.nombre} maxLength={60} onChange={(e) => setF({ ...f, nombre: e.target.value })} placeholder="Semestre 2026-2" /></label>
          <label className="campo" style={{ flex: "1 1 150px" }}>Inicio<FechaInput value={f.inicio} onChange={(e) => setF({ ...f, inicio: e.target.value })} /></label>
          <label className="campo" style={{ flex: "1 1 150px" }}>Fin<FechaInput value={f.fin} onChange={(e) => setF({ ...f, fin: e.target.value })} /></label>
          <label className="firmar"><input type="checkbox" checked={f.activo} onChange={(e) => setF({ ...f, activo: e.target.checked })} /> Activo</label>
          <button className="btn pri" disabled={a.ocupado} onClick={async () => { if (await a.correr(() => api(f.id ? "PUT" : "POST", f.id ? `/api/academico/periodos/${f.id}` : "/api/academico/periodos", f))) { setF(null); alCambiar(); } }}>Guardar</button>
          <button className="btn" onClick={() => setF(null)}>Cancelar</button>
        </div>
      )}
      {!c.periodos.length && !f && <p className="muted">Crea el periodo en curso: las matrículas y los casos se abren en el periodo activo.</p>}
      <ul className="lista">{c.periodos.map((p) => (
        <li key={p.id}><button onClick={() => setF(p)}><b>{p.nombre}</b><span>{fechaCorta(p.inicio)} al {fechaCorta(p.fin)}</span>{p.activo && <span className="chip azul" style={{ marginLeft: "auto" }}>Activo</span>}</button></li>
      ))}</ul>
      <AvisoError error={a.error} />
    </Seccion>
  );
}

function Clinicas({ c, alCambiar }: { c: Config; alCambiar: () => void }) {
  type F = { id: string; nombre: string; nivel: string; descripcion: string; activa: boolean; requisitos: { nombre: string; codigos: string[]; minimo: string }[] };
  const [f, setF] = useState<F | null>(null);
  const a = useAccion();
  const abrir = (k?: Config["clinicas"][number]) => setF(k ? { id: k.id, nombre: k.nombre, nivel: String(k.nivel), descripcion: k.descripcion, activa: k.activa, requisitos: k.requisitos.map((r) => ({ ...r, minimo: String(r.minimo) })) }
    : { id: "", nombre: "", nivel: "", descripcion: "", activa: true, requisitos: [{ nombre: "", codigos: [], minimo: "1" }] });
  const guardar = async () => {
    const cuerpo = { ...f!, nivel: Number(f!.nivel), requisitos: f!.requisitos.map((r) => ({ ...r, minimo: Number(r.minimo) })) };
    if (await a.correr(() => api(f!.id ? "PUT" : "POST", f!.id ? `/api/academico/clinicas/${f!.id}` : "/api/academico/clinicas", cuerpo))) { setF(null); alCambiar(); }
  };
  return (
    <Seccion titulo="Clínicas y requisitos mínimos" acciones={!f && <button className="btn chico pri" onClick={() => abrir()}>Nueva clínica</button>}>
      {f ? (
        <div className="form-factura">
          <div className="fila">
            <label className="campo" style={{ flex: "2 1 220px" }}>Nombre<input value={f.nombre} maxLength={80} onChange={(e) => setF({ ...f, nombre: e.target.value })} placeholder="Clínica Integral I" /></label>
            <label className="campo" style={{ flex: "0 1 120px" }}>Nivel (semestre)<input className="mono" inputMode="numeric" value={f.nivel} onChange={(e) => setF({ ...f, nivel: e.target.value.replace(/\D/g, "").slice(0, 2) })} /></label>
            <label className="campo" style={{ flex: "3 1 260px" }}>Descripción<input value={f.descripcion} maxLength={300} onChange={(e) => setF({ ...f, descripcion: e.target.value })} /></label>
          </div>
          <fieldset><legend>Requisitos: cuántos procedimientos validados necesita cada estudiante</legend>
            {f.requisitos.map((r, k) => (
              <div key={k} className="requisito-fila">
                <div className="fila" style={{ alignItems: "flex-end" }}>
                  <label className="campo" style={{ flex: "3 1 220px" }}>Requisito<input value={r.nombre} maxLength={80} onChange={(e) => setF({ ...f, requisitos: f.requisitos.map((x, j) => (j === k ? { ...x, nombre: e.target.value } : x)) })} placeholder="Resinas" /></label>
                  <label className="campo" style={{ flex: "0 1 100px" }}>Mínimo<input className="mono" inputMode="numeric" value={r.minimo} onChange={(e) => setF({ ...f, requisitos: f.requisitos.map((x, j) => (j === k ? { ...x, minimo: e.target.value.replace(/\D/g, "").slice(0, 3) } : x)) })} /></label>
                  <button type="button" className="btn chico" aria-label={`Quitar el requisito ${k + 1}`} onClick={() => setF({ ...f, requisitos: f.requisitos.filter((_, j) => j !== k) })}>✕</button>
                </div>
                <SelectorCodigos conAlta titulo="Cuentan" valor={r.codigos} aranceles={c.aranceles} alCambiar={(codigos) => setF({ ...f, requisitos: f.requisitos.map((x, j) => (j === k ? { ...x, codigos } : x)) })} />
              </div>
            ))}
            <button type="button" className="btn chico" onClick={() => setF({ ...f, requisitos: [...f.requisitos, { nombre: "", codigos: [], minimo: "1" }] })}>+ Requisito</button>
          </fieldset>
          {f.id && <label className="firmar"><input type="checkbox" checked={f.activa} onChange={(e) => setF({ ...f, activa: e.target.checked })} /> Clínica activa</label>}
          <AvisoError error={a.error} />
          <div className="fila"><button className="btn pri" disabled={a.ocupado} onClick={guardar}>Guardar clínica</button><button className="btn" onClick={() => setF(null)}>Cancelar</button></div>
        </div>
      ) : (
        <>
          {!c.clinicas.length && <p className="muted">Sin clínicas todavía.</p>}
          <ul className="lista">{c.clinicas.map((k) => (
            <li key={k.id}><button onClick={() => abrir(k)}>
              <b>{k.nombre}</b><span>Nivel {k.nivel} · {k.requisitos.length} requisitos{k.requisitos.length ? `: ${k.requisitos.map((r) => `${r.nombre} ${r.minimo}`).join(", ")}` : ""}</span>
              {!k.activa && <span className="chip" style={{ marginLeft: "auto" }}>Inactiva</span>}
            </button></li>
          ))}</ul>
        </>
      )}
    </Seccion>
  );
}

function Matriculas({ c, alCambiar }: { c: Config; alCambiar: () => void }) {
  const activo = c.periodos.find((p) => p.activo);
  const [f, setF] = useState({ periodo_id: activo?.id ?? "", acad_clinica_id: "", estudiante_id: "", tutor_id: "", grupo: "" });
  const a = useAccion();
  const del = c.matriculas.filter((m) => m.periodo_id === (f.periodo_id || activo?.id));
  return (
    <Seccion titulo="Matrículas: estudiante, clínica y tutor">
      {(!c.estudiantes.length || !c.tutores.length) && <div className="aviso info">Primero crea los usuarios con rol <b>Estudiante</b> y <b>Docente tutor/a</b> en Configuración → Usuarios y roles.</div>}
      <div className="fila" style={{ alignItems: "flex-end" }}>
        <label className="campo" style={{ flex: "1 1 160px" }}>Periodo<select value={f.periodo_id} onChange={(e) => setF({ ...f, periodo_id: e.target.value })}><option value="">—</option>{c.periodos.map((p) => <option key={p.id} value={p.id}>{p.nombre}{p.activo ? " (activo)" : ""}</option>)}</select></label>
        <label className="campo" style={{ flex: "1 1 180px" }}>Clínica<select value={f.acad_clinica_id} onChange={(e) => setF({ ...f, acad_clinica_id: e.target.value })}><option value="">—</option>{c.clinicas.filter((k) => k.activa).map((k) => <option key={k.id} value={k.id}>{k.nombre}</option>)}</select></label>
        <label className="campo" style={{ flex: "1 1 180px" }}>Estudiante<select value={f.estudiante_id} onChange={(e) => setF({ ...f, estudiante_id: e.target.value })}><option value="">—</option>{c.estudiantes.map((x) => <option key={x.id} value={x.id}>{x.nombre}</option>)}</select></label>
        <label className="campo" style={{ flex: "1 1 180px" }}>Tutor/a<select value={f.tutor_id} onChange={(e) => setF({ ...f, tutor_id: e.target.value })}><option value="">—</option>{c.tutores.map((x) => <option key={x.id} value={x.id}>{x.nombre}</option>)}</select></label>
        <label className="campo" style={{ flex: "0 1 90px" }}>Grupo<input value={f.grupo} maxLength={30} onChange={(e) => setF({ ...f, grupo: e.target.value })} /></label>
        <button className="btn pri" disabled={a.ocupado || !f.periodo_id || !f.acad_clinica_id || !f.estudiante_id || !f.tutor_id}
          onClick={async () => { if (await a.correr(() => api("POST", "/api/academico/matriculas", f))) { setF({ ...f, estudiante_id: "" }); alCambiar(); } }}>Matricular</button>
      </div>
      <AvisoError error={a.error} />
      <div className="tabla-scroll"><table className="tabla-resp">
        <thead><tr><th>Estudiante</th><th>Clínica</th><th>Tutor/a</th><th>Grupo</th><th></th></tr></thead>
        <tbody>{del.map((m) => (
          <tr key={m.id} className={m.activa ? "" : "muted"}>
            <td>{m.estudiante}</td><td>{m.clinica}</td>
            <td><select className="entrada" aria-label={`Tutor de ${m.estudiante} en ${m.clinica}`} value={m.tutor_id} onChange={async (e) => { if (await a.correr(() => api("PUT", `/api/academico/matriculas/${m.id}`, { tutor_id: e.target.value, grupo: m.grupo, activa: m.activa }))) alCambiar(); }}>
              {c.tutores.map((x) => <option key={x.id} value={x.id}>{x.nombre}</option>)}</select></td>
            <td>{m.grupo}</td>
            <td><button className="btn chico" onClick={async () => { if (await a.correr(() => api("PUT", `/api/academico/matriculas/${m.id}`, { tutor_id: m.tutor_id, grupo: m.grupo, activa: !m.activa }))) alCambiar(); }}>{m.activa ? "Dar de baja" : "Reactivar"}</button></td>
          </tr>
        ))}</tbody>
      </table></div>
      {!del.length && <p className="muted">Sin matrículas en este periodo.</p>}
    </Seccion>
  );
}

